import Markdown from '@/components/atoms/Markdown/Markdown';
import CustomButton from '@/components/atoms/CustomButton';
import MarkdownEditor from '@/components/organisms/MarkdownEditor/MarkdownEditor';
import { CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH, CONTENT_TITLE_MAX_LENGTH } from '@/constants/contentLimits';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Box, Collapse, Divider, HStack, Input, Text, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';
import ImagePicker from '../components/ImagePicker';
import { makeEmptySection, makeEmptySubsection, TArticleSectionDraft, TArticleSubsectionDraft } from '../types';

// 'edit' = author titles + markdown text + structure; 'arrange' = read-only text,
// manage images, and add/remove sections. Both share the collapsible layout.
type Mode = 'edit' | 'arrange';

type Props = {
  mode: Mode;
  sections: TArticleSectionDraft[];
  onChange: (sections: TArticleSectionDraft[]) => void;
  error?: string;
};

const sectionBoxProps = {
  backgroundColor: 'white',
  border: '1px solid',
  borderColor: 'background.separatorBlue',
  borderRadius: '12px',
  padding: '16px 18px',
} as const;

const RemoveLink = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Text
    fontSize="11px"
    fontWeight={700}
    color="extra.red"
    textTransform="uppercase"
    letterSpacing="0.4px"
    cursor="pointer"
    _hover={{ textDecoration: 'underline' }}
    onClick={onClick}
  >
    {label}
  </Text>
);

const CollapseHeader = ({
  label,
  fontSize,
  isOpen,
  onToggle,
  removeLabel,
  onRemove,
}: {
  label: string;
  fontSize: string;
  isOpen: boolean;
  onToggle: () => void;
  removeLabel: string;
  onRemove: () => void;
}) => (
  <HStack justify="space-between" align="center">
    <HStack cursor="pointer" gap="8px" onClick={onToggle}>
      {isOpen ? (
        <ChevronUpIcon boxSize={5} color="text.darkBlue" />
      ) : (
        <ChevronDownIcon boxSize={5} color="text.darkBlue" />
      )}
      <Text fontSize={fontSize} fontWeight={700} color="text.darkBlue">
        {label}
      </Text>
    </HStack>
    <RemoveLink label={removeLabel} onClick={onRemove} />
  </HStack>
);

// Read-only title box (arrange mode).
const ReadonlyTitle = ({ value }: { value: string }) => (
  <Box {...sectionBoxProps}>
    <Text color={value ? 'text.darkBlue' : 'text.mediumGray'} fontWeight={600}>
      {value || 'Untitled'}
    </Text>
  </Box>
);

// Read-only rendered text box (arrange mode).
const ReadonlyText = ({ value }: { value: string }) => (
  <Box {...sectionBoxProps}>
    <Markdown>{value || '_No content_'}</Markdown>
  </Box>
);

// Live per-block counter. The backend caps every section and subsection independently,
// so surface the limit while typing instead of failing at submit.
const TextLengthCounter = ({ value }: { value: string }) => {
  const isOverLimit = value.length > CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH;
  return (
    <Text fontSize="12px" alignSelf="flex-end" color={isOverLimit ? 'extra.red' : 'text.mediumGray'}>
      {value.length} / {CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH}
    </Text>
  );
};

const ArticleSectionsEditor = ({ mode, sections, onChange, error }: Props) => {
  const isEdit = mode === 'edit';
  // Collapsed ids (default everything expanded).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const isOpen = (id: string) => !collapsed.has(id);
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const updateSection = (id: string, patch: Partial<TArticleSectionDraft>) =>
    onChange(sections.map((s) => (s._id === id ? { ...s, ...patch } : s)));
  const removeSection = (id: string) => onChange(sections.filter((s) => s._id !== id));
  const addSection = () => onChange([...sections, makeEmptySection()]);

  const updateSubsection = (sectionId: string, subId: string, patch: Partial<TArticleSubsectionDraft>) =>
    onChange(
      sections.map((s) =>
        s._id === sectionId
          ? { ...s, subsections: s.subsections.map((sub) => (sub._id === subId ? { ...sub, ...patch } : sub)) }
          : s,
      ),
    );
  const addSubsection = (sectionId: string) =>
    onChange(
      sections.map((s) => (s._id === sectionId ? { ...s, subsections: [...s.subsections, makeEmptySubsection()] } : s)),
    );
  const removeSubsection = (sectionId: string, subId: string) =>
    onChange(
      sections.map((s) =>
        s._id === sectionId ? { ...s, subsections: s.subsections.filter((sub) => sub._id !== subId) } : s,
      ),
    );

  const renderSubsection = (sectionId: string, sub: TArticleSubsectionDraft, subIndex: number) => (
    <VStack key={sub._id} align="stretch" gap="10px" marginTop="6px">
      <CollapseHeader
        label={`Sub Section ${subIndex + 1}`}
        fontSize="15px"
        isOpen={isOpen(sub._id)}
        onToggle={() => toggle(sub._id)}
        removeLabel="Remove sub-section"
        onRemove={() => removeSubsection(sectionId, sub._id)}
      />
      <Collapse in={isOpen(sub._id)} animateOpacity>
        <VStack align="stretch" gap="10px">
          {isEdit ? (
            <Input
              {...sectionBoxProps}
              placeholder="Sub-section title"
              maxLength={CONTENT_TITLE_MAX_LENGTH}
              value={sub.title}
              onChange={(e) => updateSubsection(sectionId, sub._id, { title: e.target.value })}
            />
          ) : (
            <ReadonlyTitle value={sub.title} />
          )}
          {isEdit ? (
            <>
              <Box height="200px">
                <MarkdownEditor
                  content={sub.text}
                  setContent={(text) => updateSubsection(sectionId, sub._id, { text })}
                />
              </Box>
              <TextLengthCounter value={sub.text} />
            </>
          ) : (
            <ReadonlyText value={sub.text} />
          )}
          <ImagePicker
            existingUrl={sub.imageUrl}
            file={sub.imageFile ?? null}
            onFileChange={(imageFile) => updateSubsection(sectionId, sub._id, { imageFile })}
            fileMinWidthPx={200}
            fileMinHeightPx={200}
          />
        </VStack>
      </Collapse>
    </VStack>
  );

  const renderSection = (section: TArticleSectionDraft, index: number) => {
    // The first section is the lead — always present, no header / remove.
    const isLead = index === 0;
    const open = isLead || isOpen(section._id);

    return (
      <VStack key={section._id} align="stretch" gap="12px">
        {!isLead && <Divider />}
        {!isLead && (
          <CollapseHeader
            label={`Section ${index}`}
            fontSize="18px"
            isOpen={open}
            onToggle={() => toggle(section._id)}
            removeLabel="Remove section"
            onRemove={() => removeSection(section._id)}
          />
        )}
        <Collapse in={open} animateOpacity>
          <VStack align="stretch" gap="12px">
            {isEdit ? (
              <Input
                {...sectionBoxProps}
                placeholder="Section title"
                maxLength={CONTENT_TITLE_MAX_LENGTH}
                value={section.title}
                height={'80px'}
                px={'30px'}
                onChange={(e) => updateSection(section._id, { title: e.target.value })}
              />
            ) : (
              <ReadonlyTitle value={section.title} />
            )}
            {isEdit ? (
              <>
                <Box height="220px">
                  <MarkdownEditor content={section.text} setContent={(text) => updateSection(section._id, { text })} />
                </Box>
                <TextLengthCounter value={section.text} />
              </>
            ) : (
              <ReadonlyText value={section.text} />
            )}
            <ImagePicker
              existingUrl={section.imageUrl}
              file={section.imageFile ?? null}
              onFileChange={(imageFile) => updateSection(section._id, { imageFile })}
              fileMinWidthPx={200}
              fileMinHeightPx={200}
            />

            {section.subsections.map((sub, subIndex) => renderSubsection(section._id, sub, subIndex))}

            <HStack justify="center" py={'10px'}>
              <CustomButton
                label="+ New Sub-Section"
                backgroundColor="extra.white"
                labelColor="text.mediumGray"
                labelHoverColor="text.mediumBlue"
                width="200px"
                onClick={() => addSubsection(section._id)}
              />
            </HStack>
          </VStack>
        </Collapse>
      </VStack>
    );
  };

  return (
    <VStack align="stretch" gap="20px" width="100%" maxWidth="720px">
      {error && (
        <Text color="extra.red" fontSize="13px" textAlign="center">
          {error}
        </Text>
      )}

      {sections.length === 0 && (
        <Text color="text.mediumGray" fontSize="14px" textAlign="center">
          No sections yet. Add at least one section.
        </Text>
      )}

      {sections.map((section, index) => renderSection(section, index))}

      <Divider />
      <HStack justify="center">
        <CustomButton
          label="+ New Section"
          backgroundColor="extra.white"
          labelColor="text.mediumGray"
          labelHoverColor="text.mediumBlue"
          width="200px"
          onClick={addSection}
        />
      </HStack>
    </VStack>
  );
};

export default ArticleSectionsEditor;
